import * as Yup from 'yup'
import { filter, get, isEmpty, isNull } from 'lodash'

import {
  metaDataItemValidationSchema,
  offerStakeholdersValidation,
} from './commonFormValidation'
import {
  AzurePricingType,
  EulaType,
  IncludedQuantity,
  RecurrentPriceModeType,
} from '../../types/flyout'
import { DateTime } from 'luxon'
import {
  newLineMessage,
  emptyFieldMessage,
  priceGreaterThanZeroMessage,
  missignFieldMessage,
} from './messagesContants'
import { validateEmptyValueAndNewLines } from './validateEmptyValueAndNewLines'
import { offerEndBuyersValidationSchema } from './offerEndBuyersValidation'
import { isNonEmptyNumber } from '@labrav/common'
import './globalValidations'
import { buildOfferRecipientsValidation } from './offerRecipientYupValidation'

export { offerEndBuyersValidationSchema }

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath)

      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

Yup.addMethod(
  Yup.array,
  'installmentsValidation',
  function (propertyPath, timeZone, message) {
    return this.test('installmentsValidation', '', function (list: any) {
      const errors: any = []
      const acceptByDate = this.options?.context?.acceptBy
      const paymentDatesBeforeAcceptBy = filter(
        list,
        (item: any) =>
          item[propertyPath] &&
          DateTime.fromISO(item[propertyPath], { zone: timeZone }) <
            DateTime.fromISO(acceptByDate, { zone: timeZone })
      ).map(item => item[propertyPath])
      list?.forEach((item: any, index: number) => {
        if (
          paymentDatesBeforeAcceptBy.length > 1 &&
          paymentDatesBeforeAcceptBy.includes(item[propertyPath])
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

const recipientMessages = {
  firstNameRequired: missignFieldMessage,
  lastNameRequired: missignFieldMessage,
  emailRequired: 'Please provide the email of your offer recipient. ',
  emailInvalid: 'Enter valid email.',
  duplicateEmail: 'Duplicate emails are not allowed.',
}

export const microsoftOfferAndMPPOValidationSchemaCommon = {
  companyName: Yup.string().required(emptyFieldMessage),
  acceptBy: Yup.string().nullable().required('Provide the expiration date.'),
  emailCustomMessage: Yup.string()
    .nullable()
    .required(emptyFieldMessage)
    .test(
      'contains-new-line-characters',
      `${newLineMessage}`,
      value => value === null || validateEmptyValueAndNewLines(value)
    ),
  azurePricingType: Yup.mixed().test(
    'is-renewal-required',
    'Select a pricing type.',
    function (value: string | boolean) {
      if (value === undefined) {
        return false
      }
      return true
    }
  ),
  plans: Yup.array().of(
    Yup.object().shape({
      labraPlanId: Yup.string().required('Select a plan.'),
      discountPercentage: Yup.number()
        .nullable()
        .test('discount', 'Enter discount.', function (value) {
          return this.options?.context?.azurePricingType ===
            AzurePricingType.LABRA_PERCENTAGE
            ? !(isNull(value) || !isNonEmptyNumber(value!))
            : true
        })
        .test('discount-range', 'Enter valid discount.', function (value) {
          if (
            this.options?.context?.azurePricingType ===
              AzurePricingType.LABRA_PERCENTAGE &&
            value !== null &&
            value !== undefined &&
            isNonEmptyNumber(value)
          ) {
            return value >= 0 && value <= 100
          }
          return true
        }),
      pricing: Yup.object().shape({
        recurrentPrice: Yup.object().shape({
          recurrentPriceMode: Yup.string(),
          prices: Yup.array().of(
            Yup.object().shape({
              pricePerPaymentInUsd: Yup.number()
                .nullable()
                .test(
                  'mutual-exclusivity',
                  'Enter value (max 5 decimals, ≤100,000,000).',
                  function (value) {
                    const installments = this.parent?.installments?.schedule

                    if ((installments || [])?.length) {
                      return true
                    }
                    if (
                      !installments &&
                      (value === null || value === undefined)
                    ) {
                      return false
                    }

                    if (
                      !installments &&
                      value !== null &&
                      value !== undefined
                    ) {
                      if (value > 100000000) {
                        return false
                      }
                      const decimalPlaces = (
                        value.toString().split('.')[1] || ''
                      ).length
                      if (decimalPlaces > 5) {
                        return false
                      }
                    }

                    return true
                  }
                )
                .test(
                  'greater-than-zero',
                  priceGreaterThanZeroMessage,
                  function (value) {
                    const installments = this.parent?.installments?.schedule

                    if ((installments || [])?.length) {
                      return true
                    }

                    if (
                      !installments &&
                      value !== null &&
                      value !== undefined &&
                      value <= 0
                    ) {
                      return false
                    }

                    return true
                  }
                ),
              billingTerm: Yup.object().shape({
                value: Yup.number()
                  .typeError('Select a contract duration.')
                  .required('Select a contract duration.'),
              }),
              installments: Yup.object().when('pricePerPaymentInUsd', {
                is: (value: number) => value === null || value === undefined,
                then: Yup.object().shape({
                  tcv: Yup.number()
                    .nullable()
                    .test(
                      'mutual-exclusivity',
                      'Enter value (max 5 decimals, ≤100,000,000).',
                      function (value) {
                        if (value === null || value === undefined) {
                          return false
                        }

                        if (value !== null && value !== undefined) {
                          if (value > 100000000) {
                            return false
                          }
                          const decimalPlaces = (
                            value.toString().split('.')[1] || ''
                          ).length
                          if (decimalPlaces > 5) {
                            return false
                          }
                        }

                        return true
                      }
                    ),
                  schedule: Yup.array()
                    .of(
                      Yup.object().shape({
                        pricePerPaymentInUsd: Yup.number()
                          .typeError(
                            'Enter value (max 5 decimals, ≤100,000,000).'
                          )
                          .required(
                            'Enter value (max 5 decimals, ≤100,000,000).'
                          )
                          .max(
                            100000000,
                            'Enter value (max 5 decimals, ≤100,000,000).'
                          )
                          .checkFiveDecimalpointValidation({
                            message:
                              'Enter value (max 5 decimals, ≤100,000,000).',
                          }),
                        chargeDate: Yup.string()
                          .nullable()
                          .when('isImmediateInstallment', {
                            is: true,
                            then: Yup.string().nullable().notRequired(),
                            otherwise: Yup.string()
                              .nullable()
                              .required('Invoice date is required')
                              .test(
                                'check-all-dates-after-start-date',
                                'Invoice date cannot be before the start date.',
                                function (value?: string | null) {
                                  const offerStartDate =
                                    this.options?.context?.offerStartDate
                                  const variableStartDate =
                                    this.options?.context?.variableStartDate

                                  if (variableStartDate) return true

                                  if (!offerStartDate) return true

                                  if (value && offerStartDate) {
                                    return (
                                      DateTime.fromISO(value) >=
                                      DateTime.fromISO(offerStartDate)
                                    )
                                  }
                                  return true
                                }
                              )
                              .test(
                                'check-charge-date-within-billing-term',
                                'Invoice date cannot be after the contract duration.',
                                function (value?: string | null) {
                                  if (!value) return true

                                  const currentPlanIndex =
                                    this.path?.split('.')[0]
                                  const selectedPlan = get(
                                    this.options?.context,
                                    currentPlanIndex
                                  )

                                  const billingTerm =
                                    selectedPlan?.pricing?.recurrentPrice
                                      ?.prices[0]?.billingTerm

                                  if (
                                    !billingTerm ||
                                    !billingTerm.type ||
                                    !billingTerm.value
                                  ) {
                                    return true
                                  }

                                  const offerStartDate =
                                    this.options?.context?.offerStartDate
                                  const variableStartDate =
                                    this.options?.context?.variableStartDate

                                  const startDate =
                                    offerStartDate && !variableStartDate
                                      ? DateTime.fromISO(offerStartDate)
                                      : DateTime.now()

                                  let contractEndDate: DateTime
                                  if (billingTerm.type === 'year') {
                                    contractEndDate = startDate.plus({
                                      years: billingTerm.value,
                                    })
                                  } else if (billingTerm.type === 'month') {
                                    contractEndDate = startDate.plus({
                                      months: billingTerm.value,
                                    })
                                  } else {
                                    return true
                                  }

                                  contractEndDate = contractEndDate
                                    .minus({ days: 1 })
                                    .endOf('day')

                                  const chargeDateTime = DateTime.fromISO(value)
                                  return chargeDateTime <= contractEndDate
                                }
                              ),
                          }),
                      })
                    )
                    .test(
                      'unique-charge-dates',
                      'Duplicate invoice dates are not allowed.',
                      function (list: any) {
                        if (!list || list.length === 0) return true

                        const errors: any = []
                        const validDates = list
                          .filter(
                            (item: any) =>
                              item.chargeDate && !item.isImmediateInstallment
                          )
                          .map((item: any) => item.chargeDate)

                        const duplicateDates = validDates.filter(
                          (date: string, index: number) =>
                            validDates.indexOf(date) !== index
                        )

                        if (duplicateDates.length > 0) {
                          list.forEach((item: any, index: number) => {
                            if (duplicateDates.includes(item.chargeDate)) {
                              errors.push(
                                this.createError({
                                  path: `${this.path}[${index}].chargeDate`,
                                  message:
                                    'Duplicate invoice dates are not allowed.',
                                })
                              )
                            }
                          })
                        }

                        if (!isEmpty(errors)) {
                          throw new Yup.ValidationError(errors)
                        }

                        return true
                      }
                    )
                    .installmentsValidation(
                      'chargeDate',
                      'UTC',
                      'Only one invoice date can be before the offer expiration date.'
                    ),
                }),
                otherwise: Yup.object().nullable(),
              }),
            })
          ),
          userLimits: Yup.object().when('recurrentPriceMode', {
            is: RecurrentPriceModeType.PERUSER,
            then: Yup.object().shape({
              min: Yup.number()
                .typeError(emptyFieldMessage)
                .required(emptyFieldMessage),
              max: Yup.number()
                .typeError(emptyFieldMessage)
                .required(emptyFieldMessage)
                .test(
                  'min-max',
                  'Enter a value above min user count.',
                  function (value, context) {
                    if (
                      isNonEmptyNumber(value) &&
                      context?.parent?.min > value!
                    ) {
                      return false
                    }
                    return true
                  }
                ),
            }),
            otherwise: Yup.object().nullable(),
          }),
        }),
        customMeters: Yup.object().shape({
          meters: Yup.lazy(value => {
            if (!isEmpty(value)) {
              const validationObject = Yup.object().shape({
                includedQuantities: Yup.array().of(
                  Yup.object().shape({
                    isInfinite: Yup.boolean(),
                    quantity: Yup.number()
                      .nullable()
                      .when('isInfinite', {
                        is: false,
                        then: Yup.number()
                          .typeError('Enter base quantity.')
                          .required('Enter base quantity.'),
                      }),
                  })
                ),
                pricePerPaymentInUsd: Yup.number()
                  .nullable()
                  .when('includedQuantities', {
                    is: (includedQuantities: IncludedQuantity[]) =>
                      !includedQuantities[0]?.isInfinite,
                    then: Yup.number()
                      .typeError('Enter value (max 5 decimals, ≤100,000,000).')
                      .required('Enter value (max 5 decimals, ≤100,000,000).')
                      .max(
                        100000000,
                        'Enter value (max 5 decimals, ≤100,000,000).'
                      )
                      .checkFiveDecimalpointValidation({
                        message: 'Enter value (max 5 decimals, ≤100,000,000).',
                      }),
                  }),
              })
              const newEntries = Object.keys(value).reduce(
                (acc, val) => ({
                  ...acc,
                  [val]: validationObject,
                }),
                {}
              )

              return Yup.object().shape(newEntries)
            }
            return Yup.mixed().notRequired()
          }),
        }),
      }),
    })
  ),
  offerRecipients: Yup.array()
    .of(
      Yup.object().shape({
        firstName: Yup.string().required(emptyFieldMessage),
        lastName: Yup.string().required(emptyFieldMessage),
        title: Yup.string().nullable(),
        email: Yup.string()
          .email('Enter a valid email address.')
          .required('Enter a valid email address.'),
      })
    )
    .uniqueProperty('email', 'Duplicate emails is not allowed.'),
  metaData: Yup.array().of(metaDataItemValidationSchema),
  ...offerStakeholdersValidation,
  eulaType: Yup.string().required('Select one EULA configuration.'),
  eulaFiles: Yup.array().when('eulaType', {
    is: (eulaType: string) => eulaType === EulaType.CUSTOM,
    then: Yup.array()
      .required('EULA file is required when EULA type is custom.')
      .min(1, 'At least one EULA file is required.'),
    otherwise: Yup.array().optional().notRequired(),
  }),
  existingContractRenewal: Yup.mixed().test(
    'is-renewal-required',
    'Select Yes or No.',
    function (value: string | boolean) {
      if (value === undefined) {
        return false
      }
      return true
    }
  ),
  variableStartDate: Yup.boolean().nullable(),
  offerStartDate: Yup.string().when('variableStartDate', {
    is: false,
    then: Yup.string()
      .nullable()
      .required('Provide the purchase start month.')
      .test(
        'check speciffied date for expiration',
        'Start month cannot be before expiration date.',
        function (value?: string | null) {
          const acceptByDate = this.options?.context?.acceptBy
          if (acceptByDate) {
            return (
              DateTime.fromISO(acceptByDate) <= DateTime.fromISO(value || '')
            )
          }
          return true
        }
      ),
    otherwise: Yup.string().nullable(),
  }),
  offerExpirationDate: Yup.string()
    .nullable()
    .required('Provide the purchase end month.')
    .test(
      'check speciffied date',
      'End month cannot be before start month.',
      function (value?: string | null) {
        const offerStartDate = this.options?.context?.offerStartDate
        const variableStartDate = this.options?.context?.variableStartDate
        if (offerStartDate && !variableStartDate) {
          return (
            DateTime.fromISO(offerStartDate) <= DateTime.fromISO(value || '')
          )
        }
        return true
      }
    )
    .test(
      'check speciffied date for expiration',
      'End month cannot be before expiration date.',
      function (value?: string | null) {
        const acceptByDate = this.options?.context?.acceptBy
        if (acceptByDate) {
          return DateTime.fromISO(acceptByDate) <= DateTime.fromISO(value || '')
        }
        return true
      }
    ),
}

export const microsoftResellerAuthorizationValidationSchema = (
  isSalesforceContext: boolean
) =>
  Yup.object().shape({
    ...microsoftOfferAndMPPOValidationSchemaCommon,
    ...offerEndBuyersValidationSchema,
    beneficiaryId: Yup.string().required(emptyFieldMessage),
    mppoName: Yup.string().required(emptyFieldMessage),
    resellerDetails: Yup.array().of(
      Yup.object().shape({
        partnerName: Yup.string().required(emptyFieldMessage),
        id: Yup.number()
          .nullable()
          .typeError('Enter a valid value.')
          .required(emptyFieldMessage),
      })
    ),
    offerRecipients: buildOfferRecipientsValidation(recipientMessages),
    ...(isSalesforceContext
      ? {
          offerStakeholders: Yup.array().optional().notRequired(),
        }
      : {}),
  })

const microsoftOfferAndMPPOValidationSchema = () =>
  microsoftResellerAuthorizationValidationSchema(false)

export default microsoftOfferAndMPPOValidationSchema
